import React, { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface SetChartData {
  date: string;
  weight: number;
  reps: number;
}

interface Props {
  data: SetChartData[];
}

type ProgressRange = 'month' | '3months' | '6months' | 'all';

const RANGE_OPTIONS: {
  value: ProgressRange;
  label: string;
  months?: number;
}[] = [
  { value: 'month', label: '1M', months: 1 },
  { value: '3months', label: '3M', months: 3 },
  { value: '6months', label: '6M', months: 6 },
  { value: 'all', label: 'All' },
];

const MachineExerciseChart: React.FC<Props> = ({ data }) => {
  const [range, setRange] = useState<ProgressRange>('3months');

  const bestByWorkout = useMemo(() => {
    const byDate = new Map<string, SetChartData>();
    data.forEach(set => {
      const current = byDate.get(set.date);
      if (
        !current ||
        set.weight > current.weight ||
        (set.weight === current.weight && set.reps > current.reps)
      ) {
        byDate.set(set.date, set);
      }
    });
    return Array.from(byDate.values()).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
  }, [data]);

  const visibleData = useMemo(() => {
    const option = RANGE_OPTIONS.find(item => item.value === range);
    if (!option?.months) return bestByWorkout;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - option.months);
    const cutoffDate = cutoff.toISOString().split('T')[0];
    return bestByWorkout.filter(point => point.date >= cutoffDate);
  }, [bestByWorkout, range]);

  const latest = visibleData[visibleData.length - 1];
  const personalRecord = bestByWorkout.reduce<SetChartData | undefined>(
    (best, point) => (!best || point.weight > best.weight ? point : best),
    undefined
  );
  const first = visibleData[0];
  const change =
    first && latest && first.weight > 0
      ? Math.round(((latest.weight - first.weight) / first.weight) * 100)
      : null;

  return (
    <div className="progress-chart">
      <div className="progress-chart__ranges" aria-label="Progress time range">
        {RANGE_OPTIONS.map(option => (
          <button
            key={option.value}
            type="button"
            className={range === option.value ? 'is-selected' : ''}
            onClick={() => setRange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="progress-chart__summary">
        <div>
          <span>Current best</span>
          <strong>
            {latest ? `${latest.weight} kg × ${latest.reps}` : '—'}
          </strong>
        </div>
        <div>
          <span>Personal record</span>
          <strong>
            {personalRecord
              ? `${personalRecord.weight} kg × ${personalRecord.reps}`
              : '—'}
          </strong>
        </div>
        <div>
          <span>Period change</span>
          <strong className={change != null && change > 0 ? 'is-positive' : ''}>
            {change == null ? '—' : `${change > 0 ? '+' : ''}${change}%`}
          </strong>
        </div>
      </div>

      {visibleData.length ? (
        <ResponsiveContainer width="100%" height={230}>
          <LineChart
            data={visibleData}
            margin={{ top: 18, right: 12, left: -14, bottom: 4 }}
          >
            <CartesianGrid
              vertical={false}
              stroke="rgba(140, 133, 150, 0.22)"
              strokeDasharray="4 5"
            />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              minTickGap={24}
              tick={{ fill: 'var(--ion-color-medium)', fontSize: 10 }}
              tickFormatter={value => {
                const [, month, day] = String(value).split('-');
                return month && day ? `${day}.${month}.` : value;
              }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              width={42}
              allowDecimals={false}
              domain={['dataMin - 2', 'dataMax + 2']}
              tick={{ fill: 'var(--ion-color-medium)', fontSize: 10 }}
              tickFormatter={value => `${value} kg`}
            />
            <Tooltip
              cursor={{
                stroke: 'rgba(168, 85, 247, 0.25)',
                strokeDasharray: '4 4',
              }}
              contentStyle={{
                border: '1px solid rgba(168, 85, 247, 0.18)',
                borderRadius: 12,
                background: 'var(--ion-card-background)',
                boxShadow: 'none',
                fontSize: 12,
              }}
              formatter={(value: number, name: string, item: any) => [
                `${value} kg · ${item.payload.reps} reps`,
                'Best set',
              ]}
              labelFormatter={label => `Workout: ${label}`}
            />
            <Line
              type="monotone"
              dataKey="weight"
              stroke="var(--ion-color-primary)"
              strokeWidth={3}
              dot={{ r: 4, fill: 'var(--ion-color-primary)', strokeWidth: 0 }}
              activeDot={{
                r: 7,
                fill: 'var(--ion-color-primary)',
                stroke: 'rgba(168, 85, 247, 0.2)',
                strokeWidth: 5,
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="progress-chart__empty">No workouts in this period.</div>
      )}
    </div>
  );
};

export default MachineExerciseChart;
